import React, { useEffect, useState, useRef } from "react";

// Context
import { useAuth } from "context/Auth.context";
import { useSettings } from "context/Settings.context";

// Utils
import { formatDate } from "utils/date.utils";

// Styles
import BQMQCommentsContainer from "./BQMQComments.styles";
import Button from "components/Common/Button/Button";

interface Props {
    sessionId: string;
    factor: BQMQContextFactor;
    commentName: string;
    disabled: boolean;
    initialComments: SessionComment[];
    onCommentsChange: (factorId: string, comments: SessionComment[]) => void;
    onFormStateChange: (hasOpenForm: boolean) => void;
}

const BQMQComments = ({
    sessionId,
    factor,
    commentName,
    disabled,
    initialComments,
    onCommentsChange,
    onFormStateChange,
}: Props) => {
    const { user } = useAuth();
    const { setToast } = useSettings();

    const charLimit = 2000;

    const [comments, setComments] = useState<SessionComment[]>(
        initialComments || []
    );
    const [showAddForm, setShowAddForm] = useState(false);
    const [newComment, setNewComment] = useState("");
    const [editingCommentIndex, setEditingCommentIndex] = useState<
        number | null
    >(null);
    const [editCommentText, setEditCommentText] = useState("");
    const hasInitialized = useRef(false);

    // Initialize comments only once when component mounts or when initialComments first becomes available
    useEffect(() => {
        if (!hasInitialized.current) {
            setComments(initialComments || []);
            hasInitialized.current = true;
        }
    }, [initialComments]);

    useEffect(() => {
        const hasOpenForm = showAddForm || editingCommentIndex !== null;
        onFormStateChange(hasOpenForm);
    }, [showAddForm, editingCommentIndex]);

    const addComment = () => {
        if (!newComment.trim()) {
            setToast({
                show: true,
                message: "Please enter a comment",
                type: "error",
            });
            return;
        }

        const newCommentObj: SessionComment = {
            session_id: sessionId,
            analyst_id: user?.id || "",
            comment_text: newComment.trim(),
            parent_comment_id: null,
            is_current: true,
            created: new Date().toISOString(),
            factor: {
                id: factor.id,
                text: factor.short_name,
                created: "",
                order: 0,
            },
        } as SessionComment;

        const updatedComments = [...comments, newCommentObj];
        setComments(updatedComments);
        onCommentsChange(factor.id, updatedComments);
        setNewComment("");
        setShowAddForm(false);
    };

    const updateComment = () => {
        if (!editCommentText.trim()) {
            setToast({
                show: true,
                message: "Please enter a comment",
                type: "error",
            });
            return;
        }

        if (editingCommentIndex === null) {
            setToast({
                show: true,
                message: "Unable to update comment",
                type: "error",
            });
            return;
        }

        // Updating comments should set id as undefined, because each update should create a new version of comment
        const updatedComments = comments.map((comment, index) =>
            index === editingCommentIndex
                ? {
                      ...comment,
                      id: undefined,
                      parent_comment_id: comment.id,
                      comment_text: editCommentText.trim(),
                  }
                : comment
        );

        setComments(updatedComments);
        onCommentsChange(factor.id, updatedComments);
        setEditingCommentIndex(null);
        setEditCommentText("");
    };

    const startEdit = (comment: SessionComment, index: number) => {
        setEditingCommentIndex(index);
        setEditCommentText(comment.comment_text);
    };

    const cancelEdit = () => {
        setEditingCommentIndex(null);
        setEditCommentText("");
    };

    return (
        <BQMQCommentsContainer>
            <div className="comments-header">
                <h4>{commentName}</h4>
                {!disabled && !showAddForm && (
                    <Button
                        className="add-comment"
                        text="New comment"
                        click={() => setShowAddForm(true)}
                    />
                )}
            </div>

            {comments.length === 0 && !showAddForm && (
                <div className="no-comments">No comments yet</div>
            )}

            {showAddForm && !disabled && (
                <div className="add-comment-form">
                    <div className="char-counter">
                        <span
                            className={
                                newComment.length >= charLimit ? "limit" : ""
                            }
                        >
                            {newComment.length} / {charLimit}
                        </span>
                    </div>
                    <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Enter your comment..."
                        rows={4}
                        maxLength={charLimit}
                    />
                    <div className="form-actions">
                        <Button
                            text="Cancel"
                            click={() => {
                                setShowAddForm(false);
                                setNewComment("");
                            }}
                            color="secondary"
                        />
                        <Button
                            text="Finish editing"
                            click={addComment}
                            color="primary"
                            disabled={!newComment.trim()}
                        />
                    </div>
                </div>
            )}

            {comments.map((comment, index) => (
                <div
                    key={comment.id || `new-comment-${index}`}
                    className="comment"
                >
                    {editingCommentIndex === index ? (
                        <div className="edit-comment-form">
                            <div className="char-counter">
                                <span
                                    className={
                                        editCommentText.length >= charLimit
                                            ? "limit"
                                            : ""
                                    }
                                >
                                    {editCommentText.length} / {charLimit}
                                </span>
                            </div>
                            <textarea
                                value={editCommentText}
                                onChange={(e) =>
                                    setEditCommentText(e.target.value)
                                }
                                rows={4}
                                maxLength={charLimit}
                            />
                            <div className="form-actions">
                                <Button
                                    text="Cancel"
                                    click={cancelEdit}
                                    color="secondary"
                                />
                                <Button
                                    text="Finish editing"
                                    click={updateComment}
                                    color="primary"
                                    disabled={!editCommentText.trim()}
                                />
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="comment-header">
                                <div className="comment-meta">
                                    <span className="comment-date">
                                        {comment.created &&
                                            formatDate(
                                                new Date(comment.created)
                                            )}
                                    </span>
                                </div>
                                {!disabled && (
                                    <div className="comment-actions">
                                        <Button
                                            className="edit-comment"
                                            text="Edit"
                                            click={() =>
                                                startEdit(comment, index)
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="comment-body">
                                {comment.comment_text}
                            </div>
                        </>
                    )}
                </div>
            ))}
        </BQMQCommentsContainer>
    );
};

export default BQMQComments;
